import React, { useState, useEffect } from 'react';
import { QrCode, Download, Copy } from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = getBackendUrl() ? `${getBackendUrl()}/api` : '/api';

/**
 * Seção de QR Code do cardápio público.
 * Renderiza:
 *  - preview do QR (gerado no backend com logo + cor do tema)
 *  - botão de download PNG
 *  - botão de compartilhar no WhatsApp
 *  - botão de copiar link
 *  - toggle "incluir logo no centro"
 */
export default function QRCodeSection({ restaurant }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withLogo, setWithLogo] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const subdomain = restaurant?.subdomain || '';
  // Link de compartilhamento com PREVIEW (Open Graph) — exibe o logo da loja no WhatsApp.
  // Para crawlers entrega o logo da loja; para usuários reais redireciona ao cardápio.
  const shareLink = subdomain ? `${getBackendUrl()}/api/loja/${subdomain}` : '';

  useEffect(() => {
    let activeUrl = null;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(
          `${API}/restaurant/qrcode?size=600&with_logo=${withLogo}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        activeUrl = URL.createObjectURL(blob);
        setQrUrl(activeUrl);
      } catch (e) {
        console.error('QR gen error:', e);
        setError('Não foi possível gerar o QR Code. Tente novamente.');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [withLogo]);

  const handleDownload = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `qrcode-${subdomain || 'cardapio'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShareWhatsApp = () => {
    const text = `📱 Veja nosso cardápio digital e faça seu pedido!\n\n${restaurant?.name || ''}\n${shareLink}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener'
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('clipboard error', e);
    }
  };

  if (!subdomain) {
    return (
      <div className="mt-10 p-6 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          ⚠️ Configure um subdomínio nas Configurações da loja para gerar o QR
          Code do cardápio.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-10 bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      data-testid="qrcode-section"
    >
      <div className="flex items-start gap-3 mb-6">
        <div className="p-2.5 rounded-lg bg-emerald-100 border border-emerald-200 flex-shrink-0">
          <QrCode className="w-5 h-5 text-emerald-700" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            QR Code do Cardápio
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Imprima e cole na vitrine, balcão ou panfletos. Quando o cliente
            apontar a câmera, abre direto seu cardápio.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* QR Preview */}
        <div className="flex flex-col items-center">
          <div className="relative w-full max-w-[300px] aspect-square rounded-2xl bg-white p-3 border border-gray-200 shadow-md">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-emerald-500 rounded-full"></div>
              </div>
            ) : qrUrl ? (
              <img
                src={qrUrl}
                alt="QR Code do cardápio"
                className="w-full h-full object-contain"
                data-testid="qrcode-preview-img"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm text-center px-2">
                {error || 'Carregando QR Code...'}
              </div>
            )}
          </div>

          <label
            className="mt-4 flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none"
            data-testid="qrcode-toggle-logo"
          >
            <input
              type="checkbox"
              checked={withLogo}
              onChange={(e) => setWithLogo(e.target.checked)}
              className="w-4 h-4 rounded accent-emerald-500"
            />
            Incluir logo da loja no centro
            {!restaurant?.logo_url && (
              <span className="text-[10px] text-amber-600 ml-1">
                (sem logo cadastrada)
              </span>
            )}
          </label>
        </div>

        {/* Ações */}
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
              Link de compartilhamento (com prévia no WhatsApp)
            </p>
            <p
              className="text-sm text-emerald-700 font-mono break-all"
              data-testid="qrcode-public-url"
            >
              {shareLink}
            </p>
          </div>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!qrUrl || loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium transition-colors"
            data-testid="qrcode-download-btn"
          >
            <Download className="w-4 h-4" />
            Baixar PNG (alta resolução)
          </button>

          <button
            type="button"
            onClick={handleShareWhatsApp}
            disabled={!shareLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-50 text-white font-medium transition-colors"
            data-testid="qrcode-share-whatsapp-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            Compartilhar no WhatsApp
          </button>

          <button
            type="button"
            onClick={handleCopyLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium transition-colors"
            data-testid="qrcode-copy-link-btn"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Link copiado!' : 'Copiar link do cardápio'}
          </button>

          <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
            💡 Dica: imprima em tamanho mínimo de <strong>5x5 cm</strong> para
            a câmera ler com facilidade. Cor do QR puxa do tema da loja salvo
            acima.
          </p>
        </div>
      </div>
    </div>
  );
}
